import type { Character } from "../types/character";
import type { RoundResolution, RoundResolutionStep } from "../types/common";
import type { HorrorRaceState } from "../types/game-state";
import type { Zone } from "../types/zone";
import { clampRacePosition, reachesFinish } from "../utils/finish";
import { resolveZone } from "../utils/zone-resolver";

// Official rule: resolve zones sequentially +1 -> +2 -> +4 and end the race the
// instant a pawn reaches the final numbered cell — so later zones are not
// processed once 1st place is decided, and there is always exactly one finisher.
const ZONE_ORDER: readonly Zone[] = [1, 2, 4];

export function applyResolveRound(state: HorrorRaceState): HorrorRaceState {
  const trackLength = state.config.trackLength;
  const newPositions: Record<Character, number> = {
    ...state.characterPositions,
  };
  // T26: capture each zone's move (from/to/winner) as we resolve, so the round
  // recap can animate the +1 → +2 → +4 sequence step-by-step.
  const steps: RoundResolutionStep[] = [];

  for (const zone of ZONE_ORDER) {
    const { winner, moveDistance } = resolveZone(zone, state.zones[zone]);
    if (winner === null) {
      // Tie / empty zone — no move. Recorded so the recap shows a "no move" beat.
      steps.push({
        zone,
        winner: null,
        moveDistance: 0,
        fromPosition: 0,
        toPosition: 0,
        finished: false,
      });
      continue;
    }
    const fromPosition = newPositions[winner];
    const rawToPosition = fromPosition + moveDistance;
    const finished = reachesFinish(rawToPosition, trackLength);
    const toPosition = clampRacePosition(rawToPosition, trackLength);
    newPositions[winner] = toPosition;
    steps.push({
      zone,
      winner,
      moveDistance,
      fromPosition,
      toPosition,
      finished,
    });
    if (finished) {
      break; // race ends; remaining zones are not resolved
    }
  }

  const roundResolution: RoundResolution = { round: state.round, steps };

  // T26: HOLD in ROUND_RECAP. Apply the final pawn positions and store the
  // resolution snapshot for the client animation, and reset each player's
  // round-recap confirm flag so the all-confirmed gate starts fresh.
  //
  // If a step reached the finish, the snapshot contains only the processed zones
  // up to that finisher; later zones are intentionally skipped. applyAdvanceRound
  // then branches to RACE_SCORING instead of the next round.
  //
  // Zones stay populated during ROUND_RECAP so the client can show the exact
  // placement board that caused the movement. They are cleared/recycled by
  // applyAdvanceRound depending on whether this recap ended the race.
  return {
    ...state,
    characterPositions: newPositions,
    phase: "ROUND_RECAP",
    roundResolution,
    players: state.players.map((p) => ({
      ...p,
      hasConfirmedRoundRecap: false,
    })),
  };
}
